import {
  forEachSourceStep,
  hasStartEventInput,
  previousOutputInputs,
  someSourceStep,
  type ProcessDraftAssignment,
  type ProcessDraftStep,
  type UpdateDraftStepRequest
} from '@bliks-io/contracts';
import type { EdgeDescriptor } from '@bliks-io/ui';
import { expectSingleNode } from '@/api/draftGraph';
import { midpoint } from '../graph/position';
import type { CapturedDownstream, EdgeActionDeps } from './types';

/**
 * Look up a step by its `stableStepId` in a pre-fetched list. Stable ids are
 * `ObjectId` at rest but arrive through the cache as strings or ObjectIds
 * depending on path — coerce both sides via `String()`.
 */
export function findStepByStableId<T extends { stableStepId: ProcessDraftStep['stableStepId'] }>(
  steps: readonly T[],
  stableStepId: string
): T | undefined {
  return steps.find((s) => String(s.stableStepId) === stableStepId);
}

/**
 * Resolve a node id to a step or assignment through the draft graph. Event
 * attach/detach/upgrade targets can be assignments too — a converted step
 * keeps its event inputs, so its event edges must stay actionable. Event
 * rows themselves are never valid targets here.
 */
function findStepOrAssignment(
  deps: EdgeActionDeps,
  nodeId: string
): ProcessDraftStep | ProcessDraftAssignment | undefined {
  const entry = deps.graph.findNode(nodeId);
  return entry && entry.kind !== 'event' ? entry.node : undefined;
}

/**
 * The draft's start event node ids (`eventKind: 'start'` rows). Start-ness
 * is identity on the node — edge actions resolve it from the cached rows
 * instead of comparing against the dead `'START'` sentinel.
 */
export function startEventIdsOf(deps: Pick<EdgeActionDeps, 'getCachedEvents'>): Set<string> {
  return new Set(
    deps
      .getCachedEvents()
      .filter((ev) => ev.eventKind === 'start')
      .map((ev) => ev._id)
  );
}

/**
 * Whether a step carries an upstream edge the server-side convert accepts as
 * an assignment anchor: a sourced `previous_output` input, or an event input
 * linked to a start event (ADR 0003 P3b — a start attachment is connectivity
 * on par with a `previous_output` edge). The start-anchor arm is the shared
 * `hasStartEventInput` at its default options — the same call
 * `convert-node.ts` / `validateDraftContent` make, so the rule cannot drift.
 */
export function hasAssignmentAnchorInput(
  inputs: ProcessDraftStep['inputs'] | undefined,
  startEventIds: ReadonlySet<string>
): boolean {
  // Deliberately narrower than the backend guard on the edge arm: it accepts
  // ANY previous_output input, we require one that names a source, so a
  // half-built edge doesn't offer a convert the user then has to undo. Being
  // strictly narrower, it never offers a convert the backend would reject.
  return (
    previousOutputInputs(inputs).some((i) => (i.sourceSteps?.length ?? 0) > 0) ||
    hasStartEventInput(inputs, startEventIds)
  );
}

/**
 * Optimistic stand-in for the start-event input the backend writes on attach
 * (`eventId` link + denormalized event snapshot). Snapshot fields fall back to
 * blanks when the event row isn't in the cache yet — the reconcile refetch
 * replaces them with the server's copy.
 */
export function optimisticStartEventInput(
  deps: Pick<EdgeActionDeps, 'getCachedEvents'>,
  startEventId: string
): ProcessDraftStep['inputs'][number] {
  const startEvent = deps.getCachedEvents().find((ev) => ev._id === startEventId);
  return {
    inputType: 'event',
    eventId: startEventId,
    type: startEvent?.eventType ?? 'none',
    name: startEvent?.name ?? '',
    description: startEvent?.description ?? ''
  };
}

/**
 * The start event node the given node is attached to, in ANY representation —
 * the canonical `eventId`/`previousStep` forms or the legacy `'START'`
 * sentinel forms a not-yet-republished draft still carries (those resolve to
 * the primary start event). Null when the node is not start-attached.
 */
export function attachedStartEventId(
  node: { previousStep?: unknown; inputs?: ProcessDraftStep['inputs'] },
  startEventIds: ReadonlySet<string>
): string | null {
  for (const input of node.inputs ?? []) {
    if (input.inputType === 'event' && input.eventId && startEventIds.has(String(input.eventId))) {
      return String(input.eventId);
    }
  }
  const prev = node.previousStep;
  if (prev != null && prev !== 'START' && startEventIds.has(String(prev))) {
    return String(prev);
  }
  const primaryStartId: string | null = startEventIds.values().next().value ?? null;
  if (prev === 'START') return primaryStartId;
  for (const input of node.inputs ?? []) {
    if (inputHasStartSource(input, startEventIds)) return primaryStartId;
  }
  return null;
}

/** Boolean form of {@link attachedStartEventId}. */
export function nodeIsStartAttached(
  node: { previousStep?: unknown; inputs?: ProcessDraftStep['inputs'] },
  startEventIds: ReadonlySet<string>
): boolean {
  return attachedStartEventId(node, startEventIds) !== null;
}

/**
 * Returns the `sourceSteps` array for an input, regardless of input type.
 * Event inputs don't carry `sourceSteps` in the Zod schema, but the backend
 * seeds first steps by tagging their event input with `sourceSteps: [{ stepId: 'START' }]`
 * (an out-of-schema field Mongoose accepts). Treat that tag the same as a
 * previous_output source when deciding START membership.
 */
function readSourceSteps(
  input: ProcessDraftStep['inputs'][number]
): { stepId: unknown; ruleIds?: unknown[] }[] {
  const candidate = (input as { sourceSteps?: unknown }).sourceSteps;
  return Array.isArray(candidate) ? (candidate as { stepId: unknown; ruleIds?: unknown[] }[]) : [];
}

export function inputHasStartSource(
  input: ProcessDraftStep['inputs'][number],
  startEventIds?: ReadonlySet<string>
): boolean {
  return readSourceSteps(input).some(
    (s) => String(s.stepId) === 'START' || (startEventIds?.has(String(s.stepId)) ?? false)
  );
}

/**
 * Remove the START source from each input. Semantics differ by input type:
 *  - `event` inputs keep their fields but drop the `sourceSteps` tag (so the
 *    step can later be reconnected without losing its event metadata). Pass
 *    `{ dropEventInput: true }` for flows that *replace* the START connection
 *    rather than preserve it — the event input is then removed entirely so a
 *    follow-up `connect` doesn't leave the step with two input rows.
 *  - `previous_output` inputs with only START as source are dropped entirely
 *    (they exist purely to carry the START tag — they have no meaning
 *    without it).
 *  - `previous_output` inputs with START + other sources drop just the START
 *    entry from `sourceSteps`.
 */
export function stripStartSource(
  inputs: ProcessDraftStep['inputs'],
  options: { dropEventInput?: boolean; startEventIds?: ReadonlySet<string> } = {}
): ProcessDraftStep['inputs'] {
  const isStartRef = (s: { stepId: unknown }): boolean =>
    String(s.stepId) === 'START' || (options.startEventIds?.has(String(s.stepId)) ?? false);
  const out: ProcessDraftStep['inputs'] = [];
  for (const input of inputs ?? []) {
    // Canonical start attachment: the eventId-linked event input.
    if (
      input.inputType === 'event' &&
      input.eventId &&
      options.startEventIds?.has(String(input.eventId))
    ) {
      continue;
    }
    const sources = readSourceSteps(input);
    const hasStart = sources.some(isStartRef);
    if (!hasStart) {
      out.push(input);
      continue;
    }
    if (input.inputType === 'event') {
      if (options.dropEventInput) continue;
      const { sourceSteps: _omit, ...rest } = input as typeof input & {
        sourceSteps?: unknown;
      };
      out.push(rest as typeof input);
      continue;
    }
    const filtered = sources.filter((s) => !isStartRef(s));
    if (filtered.length === 0) continue;
    out.push({
      ...(input as object),
      sourceSteps: filtered
    } as typeof input);
  }
  return out;
}

export function resolvePosition(
  edge: EdgeDescriptor,
  deps: EdgeActionDeps
): { x: number; y: number } {
  // Edge endpoints carry `stepId` which is the React Flow node.id = stableStepId.
  // Missing map entries are the new "unpositioned" sentinel (see note on
  // `getPositionsById`).
  const positions = deps.getPositionsById();
  const src = edge.source.stepId ? positions.get(edge.source.stepId) : undefined;
  const tgt = edge.target.stepId ? positions.get(edge.target.stepId) : undefined;
  if (src && tgt) return midpoint(src, tgt);
  if (src) return { x: src.x + 160, y: src.y };
  if (tgt) return { x: tgt.x - 160, y: tgt.y };
  return { x: 0, y: 0 };
}

export interface SuccessorStatusPatchOptions {
  /**
   * When true, replace persons/orgFunctions/systems/externalContacts with empty
   * arrays — terminal steps don't deliver work to anyone, so stale entries
   * would render successor chips for a node with no successor.
   */
  clearSuccessorArrays?: boolean;
}

export function taskPatchWithSuccessorStatus(
  step: ProcessDraftStep,
  successorStatus: string,
  options: SuccessorStatusPatchOptions = {}
): UpdateDraftStepRequest {
  const task = (step.task ?? {}) as UpdateDraftStepRequest['task'];
  const baseSuccessors = options.clearSuccessorArrays
    ? { persons: [], orgFunctions: [], systems: [], externalContacts: [] }
    : (task?.successors ?? {});
  return {
    task: {
      ...(task ?? {}),
      successors: {
        ...baseSuccessors,
        successorStatus
      }
    } as UpdateDraftStepRequest['task']
  };
}

export function rulesPatchWithSuccessorStatus(
  step: ProcessDraftStep,
  ruleId: string,
  successorStatus: string,
  options: SuccessorStatusPatchOptions = {}
): UpdateDraftStepRequest {
  return rulesPatchWithStatuses(step, new Map([[ruleId, successorStatus]]), options);
}

/**
 * Build a rules patch that updates the successor status on every rule named
 * in `statusByRuleId` to the mapped value. Rules not in the map pass through
 * untouched. Used by the per-handle terminal flip, where a single output
 * handle can carry multiple rules (grouped by `output`) and must update them
 * together so one undo reverts the whole group.
 *
 * Each rule in the map can carry a different target status — the reverse
 * (undo) patch uses this form to restore each rule's own prior status.
 */
export function rulesPatchWithStatuses(
  step: ProcessDraftStep,
  statusByRuleId: ReadonlyMap<string, string>,
  options: SuccessorStatusPatchOptions = {}
): UpdateDraftStepRequest {
  const rules = (step.rules ?? []) as NonNullable<UpdateDraftStepRequest['rules']>;
  return {
    rules: rules.map((rule) => {
      const id = (rule as { _id?: unknown })._id;
      if (!id) return rule;
      const nextStatus = statusByRuleId.get(String(id));
      if (nextStatus === undefined) return rule;
      const r = rule as { successors?: { successorStatus?: string } };
      const baseSuccessors = options.clearSuccessorArrays
        ? { persons: [], orgFunctions: [], systems: [], externalContacts: [] }
        : (r.successors ?? {});
      return {
        ...(rule as object),
        successors: {
          ...baseSuccessors,
          successorStatus: nextStatus
        }
      } as NonNullable<UpdateDraftStepRequest['rules']>[number];
    })
  };
}

export function currentSuccessorStatus(step: ProcessDraftStep, ruleId: string | undefined): string {
  if (ruleId) {
    const rules = (step.rules ?? []) as Array<{
      _id?: unknown;
      successors?: { successorStatus?: string };
    }>;
    const rule = rules.find((r) => r._id && String(r._id) === ruleId);
    return rule?.successors?.successorStatus ?? '';
  }
  const task = step.task as { successors?: { successorStatus?: string } } | undefined;
  return task?.successors?.successorStatus ?? '';
}

function collectInputSources(step: ProcessDraftStep): { stepId: string; ruleId?: string }[] {
  const out: { stepId: string; ruleId?: string }[] = [];
  forEachSourceStep(step.inputs, (src) => {
    out.push({ stepId: String(src.stepId), ruleId: src.ruleIds?.[0] });
  });
  return out;
}

export function collectDownstreamOf(
  sourceStableId: string,
  allSteps: ProcessDraftStep[],
  excludeStepId: string
): CapturedDownstream[] {
  const captured: CapturedDownstream[] = [];
  for (const s of allSteps) {
    const stable = String(s.stableStepId);
    if (stable === excludeStepId) continue;
    for (const src of collectInputSources(s)) {
      if (src.stepId === sourceStableId) {
        captured.push({ targetId: stable, ruleId: src.ruleId });
      }
    }
  }
  return captured;
}

export function nodeHasSource(
  node: { inputs?: ProcessDraftStep['inputs'] | ProcessDraftAssignment['inputs'] },
  sourceStableId: string
): boolean {
  return someSourceStep(node.inputs, (src) => String(src.stepId) === sourceStableId);
}

/**
 * Stricter form of `nodeHasSource` — the node must reference the given
 * upstream AND tag that reference with any of the given rule ids. A handle
 * backs every rule of its output group, so a node linked to any of them
 * hangs off that handle.
 */
export function nodeHasAnyRuleSource(
  node: { inputs?: ProcessDraftStep['inputs'] | ProcessDraftAssignment['inputs'] },
  sourceStableId: string,
  ruleIds: ReadonlySet<string>
): boolean {
  if (ruleIds.size === 0) return false;
  return someSourceStep(
    node.inputs,
    (src) =>
      String(src.stepId) === sourceStableId &&
      (src.ruleIds ?? []).some((r) => ruleIds.has(String(r)))
  );
}

export function hasDownstreamAssignment(
  sourceStableId: string,
  draftAssignments: readonly ProcessDraftAssignment[]
): boolean {
  return draftAssignments.some((a) => nodeHasSource(a, sourceStableId));
}

/**
 * Whether any draft step or assignment consumes this node's output — i.e. the
 * node is NOT a leaf. The shared leaf-detection predicate behind both
 * "Convert to assignment" (a placeholder can't own authored downstream work)
 * and "Merge into…" (merge is offered on leaf nodes only).
 */
export function hasDownstream(
  sourceStableId: string,
  steps: ProcessDraftStep[],
  draftAssignments: readonly ProcessDraftAssignment[]
): boolean {
  return (
    collectDownstreamOf(sourceStableId, steps, sourceStableId).length > 0 ||
    hasDownstreamAssignment(sourceStableId, draftAssignments)
  );
}

/**
 * Attach a first-class event to a step via the `/events/:eventId/attach`
 * endpoint — the ONE attach action since ADR 0003 P3b: dropping the start
 * event on a step goes through here too (the backend marks first-step-ness
 * by setting `previousStep = <startEventNodeId>` alongside the `eventId`
 * input link). Returns whether the server action fired (false when we
 * already have a matching input in the cache — idempotency on double-fire).
 */
export async function attachEventToStep(
  eventId: string,
  targetStepId: string,
  deps: EdgeActionDeps
): Promise<boolean> {
  const target = findStepOrAssignment(deps, targetStepId);
  if (!target) return false;
  const alreadyAttached = (target.inputs ?? []).some(
    (input) => input.inputType === 'event' && input.eventId && String(input.eventId) === eventId
  );
  if (alreadyAttached) return false;
  await deps.attachEvent({ eventId, targetStepId: target._id });
  return true;
}

/**
 * Parse a legacy event node id of the form `event-<stableStepId>-<inputIdx>`.
 * The flow builder mints these ids using the display adapter's `_id` (which
 * is actually the owning step's `stableStepId` — the adapter swaps the real
 * step `_id` for `stableStepId` so React Flow node ids match the edge layer's
 * stableStepId convention). Returns null for canonical `event-<eventId>` ids
 * (bare 24-char hex tail, no dash) and for non-event ids.
 */
function parseLegacyEventNodeId(
  nodeId: string
): { stableStepId: string; inputIndex: number } | null {
  const prefix = 'event-';
  if (!nodeId.startsWith(prefix)) return null;
  const tail = nodeId.slice(prefix.length);
  const lastDash = tail.lastIndexOf('-');
  if (lastDash <= 0) return null;
  const stableStepId = tail.slice(0, lastDash);
  const idxStr = tail.slice(lastDash + 1);
  if (!/^[a-f\d]{24}$/i.test(stableStepId)) return null;
  if (!/^\d+$/.test(idxStr)) return null;
  return { stableStepId, inputIndex: Number(idxStr) };
}

/**
 * Promote a legacy event input into a first-class `ProcessDraftEvent` and
 * return the new event's id. Idempotent: returns null for node ids that
 * aren't the legacy `event-<stableStepId>-<inputIdx>` form (canonical ids pass
 * `parseEventNodeId` instead, and non-event ids shouldn't reach this path).
 *
 * Resolves the owning step from the cache via `stableStepId`, then sends the
 * step document's real `_id` to the backend (which routes by `_id`).
 */
export async function upgradeLegacyEventInput(
  legacyNodeId: string,
  deps: EdgeActionDeps
): Promise<string | null> {
  const parsed = parseLegacyEventNodeId(legacyNodeId);
  if (!parsed) return null;
  const ownerStep = findStepOrAssignment(deps, parsed.stableStepId);
  if (!ownerStep) return null;

  const envelope = await deps.upgradeLegacyEventInput({
    stepId: ownerStep._id,
    inputIndex: parsed.inputIndex
  });
  // The event's public id IS the row's stableStepId (storage _id never leaves
  // the backend).
  return String(expectSingleNode(envelope, 'event').stableStepId);
}

export async function detachEventFromStep(
  eventId: string,
  targetStepId: string,
  deps: EdgeActionDeps
): Promise<boolean> {
  const target = findStepOrAssignment(deps, targetStepId);
  if (!target) return false;
  const hasMatching = (target.inputs ?? []).some(
    (input) => input.inputType === 'event' && input.eventId && String(input.eventId) === eventId
  );
  // Detaching the START event must also fire for legacy-shaped first steps
  // (previousStep sentinel / START tags, no eventId input yet) — the backend
  // clears every linkage form in one call.
  const startEventIds = startEventIdsOf(deps);
  const isLegacyStartDetach =
    !hasMatching && startEventIds.has(eventId) && nodeIsStartAttached(target, startEventIds);
  if (!hasMatching && !isLegacyStartDetach) return false;
  await deps.detachEvent({ eventId, stepId: target._id });
  return true;
}
